/**
 * Primary kid-facing button. Three visual sizes (`large`, `medium`,
 * `small`) and two variants (`primary` filled, `secondary` ghost).
 *
 * `large` is the home-screen Play Today CTA — the focal action of the
 * screen. `medium` is a normal CTA. `small` is for tucked secondary
 * actions ("Sign out this device" etc).
 *
 * All sizes respect spec §9's 56px touch-target minimum. Press feedback
 * is the snappy scale-down spring per design.md §7.
 *
 * Sound (design.md §8 "button confirm / Done"):
 *   Primary-variant clicks play the soft felt-mallet tap cue. Secondary
 *   and danger variants stay silent — they're cancels / dismissals /
 *   destructive choices, where a confirm-style sound would feel wrong.
 *   The mute setting is owned by lib/sounds.ts and gates all playback.
 *   Pass `silent` to suppress on a specific primary button when needed
 *   (currently nothing does — kept as an escape hatch).
 */
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from "react";

import { playTap } from "@/lib/sounds";
import "./Button.css";

export type ButtonSize = "large" | "medium" | "small";
export type ButtonVariant = "primary" | "secondary" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  /** Opt out of the auto-tap sound for primary buttons. Rare. */
  silent?: boolean;
  children: ReactNode;
}

export function Button({
  size = "medium",
  variant = "primary",
  silent = false,
  className,
  children,
  type = "button",
  onClick,
  ...rest
}: ButtonProps) {
  const classes = ["btn", `btn--${size}`, `btn--${variant}`, className]
    .filter(Boolean)
    .join(" ");

  function handleClick(e: MouseEvent<HTMLButtonElement>) {
    if (variant === "primary" && !silent && !e.defaultPrevented) {
      void playTap();
    }
    onClick?.(e);
  }

  return (
    <button {...rest} type={type} className={classes} onClick={handleClick}>
      {children}
    </button>
  );
}
